import { useLayoutEffect, useState } from 'react';
import {
  clearWizardState,
  restoreWizardState,
  useWizardPersistence,
} from './use-wizard-persistence';

import { CaptureFields } from './use-daily-log-handler';

export interface VisitorLogState extends CaptureFields {
  visitorType: string;
  preAuthorized: string;
  numberOfGuests: number;
  // WP-4.4. The visitor log has no host NAME field — the host was only ever
  // apartmentLocation plus unit_id — so this does not disambiguate an existing
  // string, it turns "which apartment" into "which person in it".
  //
  // The GUEST names (names[]/lastNames[]) deliberately have no equivalent: a
  // visitor is an outsider, and linking one to an occupancy would be asserting
  // something false. They get provenance only.
  host_name: string;
  host_occupancy_id: string | null;
  host_resolution: 'unset' | 'single_match' | 'picked' | 'no_match';
  apartmentLocation: string;
  approval: string;
  parkingValidation: string;
  notes: string;
  details: string;
  description: string;
  names: string[];
  // Index-aligned with names[]/lastNames[] — one entry per guest.
  middleNames: string[];
  lastNames: string[];
  // camera
  capturedImage: string | null;
  isStreaming: boolean;
  isBlurry: boolean;
  userId: string | undefined;
  visitorDetails: string;
  property_ids: number[] | [] | undefined;
}

/** The sessionStorage bucket this wizard owns. */
const WIZARD_FLOW = 'visitor';

const useVisitorLogHandler = (
  userId: string | undefined,
  property_ids: number[] | [] | undefined
) => {
  const initialState: VisitorLogState = {
    preAuthorized: '',
    numberOfGuests: 1,
    host_name: '',
    host_occupancy_id: null,
    host_resolution: 'unset',
    unit_id: null,
    field_provenance: {},
    apartmentLocation: '',
    approval: '',
    parkingValidation: '',
    notes: '',
    details: '',
    description: '',
    names: [''],
    middleNames: [''],
    lastNames: [''],
    capturedImage: null,
    isStreaming: true,
    isBlurry: false,
    userId: userId,
    visitorType: '',
    visitorDetails: '',
    property_ids: property_ids || [],
  };

  /* RESTORED BEFORE THE FIRST PAINT, not in an effect afterwards.
     A lazy initialiser means the wizard never renders empty and then jumps —
     the reader either sees their entry or sees a blank form, never both. */
  const [visitorLogState, setVisitorLogState] = useState<VisitorLogState>(
    () => {
      const restored = restoreWizardState<VisitorLogState>(
        WIZARD_FLOW,
        userId
      );

      // The stored draft wins for what was TYPED, but never for identity or
      // scope: userId and property_ids come from the live session, so a draft
      // cannot carry a stale building or somebody else's id back into a write.
      return restored
        ? { ...initialState, ...restored, userId, property_ids: property_ids || [] }
        : initialState;
    }
  );
  const activePropertyId = property_ids?.[0] ?? null;

  // Heal the state SHAPE once on mount.
  //
  // The three guest arrays are index-aligned — names[i], middleNames[i] and
  // lastNames[i] are one person, and the provenance keys ("names.0") depend on
  // that. middleNames arrived later than the other two, so a state object
  // carried over by hot reload, or restored from a draft saved before it
  // existed, is missing it entirely.
  //
  // Repaired here rather than only guarded at each use site: a half-shaped
  // state is a bug waiting at every future call site too, and the arrays are
  // also padded to a common length so an index that exists in one exists in
  // all three.
  useLayoutEffect(() => {
    setVisitorLogState(prev => {
      const names = prev.names || [''];
      const middleNames = prev.middleNames || [];
      const lastNames = prev.lastNames || [''];
      const n = Math.max(names.length, middleNames.length, lastNames.length, 1);
      const pad = (a: string[]) =>
        a.length === n ? a : [...a, ...Array(n - a.length).fill('')];

      if (
        prev.middleNames &&
        names.length === n &&
        middleNames.length === n &&
        lastNames.length === n
      ) {
        return prev;
      }

      return {
        ...prev,
        names: pad(names),
        middleNames: pad(middleNames),
        lastNames: pad(lastNames),
      };
    });
  }, []);

  useLayoutEffect(() => {
    const nextPropertyIds = activePropertyId === null ? [] : [activePropertyId];

    setVisitorLogState(previous => {
      const propertyIdUnchanged =
        (previous.property_ids?.[0] ?? null) === activePropertyId;

      if (propertyIdUnchanged && previous.userId === userId) {
        return previous;
      }

      return {
        ...previous,
        userId,
        property_ids: nextPropertyIds,
      };
    });
  }, [activePropertyId, userId]);

  const visitorChangeHandler = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      | string,
    fieldName?: string,
    explicitValue?: string,
    index?: number
  ) => {
    let value: string;

    if (explicitValue !== undefined) {
      value = explicitValue;
    } else if (typeof e === 'string') {
      value = e;
    } else if ('value' in e.target) {
      value = e.target.value;
    } else {
      throw new Error('Unable to determine the value to set.');
    }

    if (
      (fieldName === 'names' ||
        fieldName === 'middleNames' ||
        fieldName === 'lastNames') &&
      value
    ) {
      setVisitorLogState(prevState => {
        // Same reason as addNewName: a state shape from before middleNames
        // existed has undefined here, and spreading it throws inside a state
        // updater.
        const updatedField = [
          ...(prevState[fieldName as 'names' | 'middleNames' | 'lastNames'] ||
            []),
        ];
        if (index !== undefined) {
          // <-- Use the provided index or default to the last element
          updatedField[index] = value;
        } else {
          updatedField[updatedField.length - 1] = value; // Update the last element
        }

        return {
          ...prevState,
          [fieldName]: updatedField,
        };
      });
    } else if (fieldName) {
      setVisitorLogState(prevState => ({
        ...prevState,
        [fieldName]: value,
      }));
    }
  };

  // Patch setter for the capture fields — see the note in
  // use-daily-log-handler. changeHandler is string-only by construction and
  // these are a number, a nullable uuid and an object.
  const setCapture = (patch: Partial<VisitorLogState>) => {
    setVisitorLogState(prev => ({ ...prev, ...patch }));
  };

  const visitorFinish = () => {
    setVisitorLogState(initialState);
    // The draft dies with the entry it belonged to. Leaving it would restore a
    // finished visit over the next one.
    clearWizardState(WIZARD_FLOW, userId);
  };

  const addNewName = (fieldName: 'names' | 'middleNames' | 'lastNames') => {
    setVisitorLogState(prevState => ({
      ...prevState,
      // `|| []` is load-bearing, not defensive noise. The guest arrays are a
      // SET that must grow together, and middleNames was added later — so any
      // state shaped before it existed has `undefined` there. That includes a
      // hot-reloaded session and a restored draft. Spreading undefined throws,
      // and it throws inside a setState updater during render, which takes the
      // whole tree down rather than failing one field.
      [fieldName]: [...(prevState[fieldName] || []), ''],
    }));
  };

  /* "Nothing typed yet" for THIS flow. The array fields start as [''] rather
     than [], so a length check would call an untouched form dirty and persist a
     blank draft on every reload. */
  const isEmptyEntry =
    !visitorLogState.visitorType &&
    !(visitorLogState.names || []).some(v => (v || '').trim()) &&
    !(visitorLogState.lastNames || []).some(v => (v || '').trim()) &&
    !visitorLogState.apartmentLocation &&
    !visitorLogState.notes &&
    !visitorLogState.details;

  useWizardPersistence(WIZARD_FLOW, visitorLogState, isEmptyEntry, userId);

  return {
    visitorLogState,
    visitorChangeHandler,
    visitorFinish,
    addNewName,
    setCapture,
  };
};

export default useVisitorLogHandler;
